import { useState, useCallback, useContext } from 'react'
import cx from 'classnames'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { FolderClient } from 'api/clients/FolderClient'
import { Modal } from 'antd'
import isEqual from 'lodash/isEqual'

import { AlgoliaArticle, AlmanacFile, Permission, RootState } from 'types'
import { redirectToLogin, getArticlePath } from 'utils/urlUtils'
import { handbookLink } from 'components/Handbook2_0/util'
import { ReactComponent as BackIcon16 } from 'assets/icons/arrow--left_16.svg'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

import CreateFromTemplateSidePanel from './CreateFromTemplateSidePanel'
import { TemplatePreview } from './TemplatePreview'

import Styles from './CreateFromTemplateModal.module.scss'
import { ReactComponent as CloseIcon } from 'assets/icons/close24.svg'

import { Button } from '../Button'
import { EditorStateContext } from 'common/Prose/EditorProvider/EditorProviderContext'
import { useArticle } from '../../hooks/queries/articles/useArticle'
import { EditorView } from 'prosemirror-view'
import { EditorState } from 'prosemirror-state'
import useTemplateCategories from './hooks/useTemplateCategories'
import templatesStore from 'stores/TemplatesStore'
import { observer } from 'mobx-react-lite'
import { useIsPwa } from 'utils/isPwa'
import { sendMessageToRoot } from 'screens/TabRoot/utils'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'

interface CreateFromTemplateModalProps {
  visible: boolean
  onClose?: () => void
  onGoBack?: ({ overrideFolder }: { overrideFolder?: AlmanacFile | null }) => void
  overrideFolder?: AlmanacFile | null
  overrideWorkspaceId?: number
  editorState?: EditorState
  editorView?: EditorView
}

/**
 * CreateFromTemplateModal
 *  - Allows a user to copy a public article (template) into their workspace
 */
const CreateFromTemplateModal = observer(
  ({ visible = false, onClose, onGoBack, overrideFolder, overrideWorkspaceId }: CreateFromTemplateModalProps) => {
    const auth = useSelector(({ auth }: RootState) => auth, isEqual)
    const history = useHistory()
    const isPWA = useIsPwa()
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const [workspaceId] = useState(overrideWorkspaceId || auth?.company_id!)
    const folderId = overrideFolder?.id || null

    const editorState = useContext(EditorStateContext)

    const articleQuery = useArticle(templatesStore.selectedModalTemplate as string, {
      enabled: !!templatesStore.selectedModalTemplate,
    })

    const currentArticle = useCurrentArticleMeta()
    const canEditCurrentArticle = currentArticle?.current_user_access === Permission.Edit

    const { selectedCategory, selectedPickerTemplate, selectedModalTemplate } = templatesStore

    const goBack = () => {
      onGoBack?.({ overrideFolder })
      onClose?.()
    }

    const categoriesQuery = useTemplateCategories({
      onSuccess: (data) => {
        if (!selectedModalTemplate) {
          const _selectedCategory =
            data.find((cat: any) => cat.name === selectedCategory || cat.name.toLowerCase().includes('popular')) ||
            data[0]

          templatesStore.setState({
            selectedModalTemplate:
              _selectedCategory?.articles.find((a: AlgoliaArticle) => a.key === selectedPickerTemplate)?.key ||
              _selectedCategory?.articles[0]?.key ||
              null,
          })
        }
      },
    })

    /**
     * Copy selected article into the root of current workspace
     */
    const handleCopyTemplate = async () => {
      if (!templatesStore.selectedModalTemplate) return

      if (!auth || !auth.confirmed) {
        templatesStore.closeModal()
        return redirectToLogin(history)
      }

      // copy into workspace all files
      const response = await FolderClient.bulkCopyFiles({
        folderIds: [],
        articleIds: [templatesStore.selectedModalTemplate],
        fileIds: [],
        destinationFolderId: folderId,
        destinationWorkspaceId: workspaceId,
      })

      // close modal and redirect to new article's editor
      if (response.type === SUCCESS_RESPONSE_TYPE) {
        templatesStore.closeModal()

        const path = !!overrideFolder
          ? handbookLink(overrideFolder.handbook || '', response.data.files[0].slug, true)
          : // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            getArticlePath({ slug: response.data.files[0].slug! })
        if (isPWA) {
          sendMessageToRoot({ type: 'AddTab', url: path, switchToTab: true })
        } else {
          window.open(path)
        }
        return
      }

      showErrorMessageToast('Something went wrong')
    }

    const handleReplaceDocWithTemplate = useCallback(async () => {
      // TemplateMiniCardDoc components listens for this change to update the editor
      templatesStore.setState({
        selectedPickerTemplate: templatesStore.selectedModalTemplate,
        trackTemplateUsage: true,
      })

      templatesStore.closeModal()
    }, [templatesStore.selectedModalTemplate]) // eslint-disable-line react-hooks/exhaustive-deps

    if (articleQuery.isIdle) {
      return null
    }

    return (
      <div>
        <Modal
          visible={visible}
          onCancel={onClose}
          wrapClassName={Styles.container}
          footer={null}
          closable={false}
          width={1060}
          bodyStyle={{ height: '90vh' }}
        >
          <div className={cx(Styles.header, { [Styles.withBackButton]: !!onGoBack })}>
            <div className={Styles.titleContainer}>
              {!!onGoBack && (
                <Button variant="tertiary" onPress={goBack}>
                  <BackIcon16 />
                </Button>
              )}
              <div>Start From Template</div>
            </div>
            <div className={Styles.dFlex}>
              <button className={Styles.closeButton} onClick={onClose}>
                <CloseIcon />
              </button>
            </div>
          </div>
          <div className={Styles.body}>
            <div className={Styles.leftSideBar}>
              <CreateFromTemplateSidePanel
                loading={categoriesQuery.status}
                categories={categoriesQuery.data || []}
                setSelectedArticleId={(articleId: string) => {
                  templatesStore.setState({ selectedModalTemplate: articleId })
                }}
                selectedArticleId={templatesStore.selectedModalTemplate}
              />
            </div>
            <div className={Styles.rightSideBar}>
              <TemplatePreview articleId={templatesStore.selectedModalTemplate as string} />
              <div className={Styles.footer}>
                {!templatesStore.isTemplateModalOpenFromPicker && (
                  <Button variant={!editorState ? 'primary' : 'secondary'} size="lg" onPress={handleCopyTemplate}>
                    Create new doc from template
                  </Button>
                )}

                {editorState && canEditCurrentArticle && (
                  <Button variant={'primary'} size="lg" onPress={handleReplaceDocWithTemplate}>
                    {templatesStore.isTemplateModalOpenFromPicker
                      ? `Use this template`
                      : `Replace current doc with template`}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Modal>
      </div>
    )
  },
)

export default CreateFromTemplateModal
